import React, { useState, useEffect, useRef } from 'react';
import ListLoadError from '../../components/ListLoadError';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import FinancialsEmptyState from '../../components/Invoicing/FinancialsEmptyState';
import { extractApiError } from '../../utils/errorMessages';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faPlus, faEye, faCheckCircle, faCheck, faSync, faEdit, faTrash, faClipboardList, faDownload, faFilePdf, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import { useFlashMessage } from '../../components/FlashMessage/FlashMessageContext';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import Footer from '../../components/ui/Footer';
import invoicingService from '../../services/invoicingService';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceSearchFilter from '../../components/Invoicing/InvoiceSearchFilter';
import InvoiceRelationshipModal from '../../components/Invoicing/InvoiceRelationshipModal';
import './OutgoingInvoice.css';
import SortableTh from '../../components/shared/SortableTh';
import useTableSort from '../../components/shared/useTableSort';
import useSavedRowFlash from '../../components/shared/useSavedRowFlash';
import SavedRowBanner from '../../components/shared/SavedRowBanner';
import { DEBIT_NOTE_COLUMNS } from './listColumns';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';

/**
 * Debit Note List Page
 * 
 * Features:
 * - List all debit notes with filtering
 * - Advanced search and filters
 * - Track supplier reduction claims (AP-side)
 * - View, edit, and manage debit notes
 */


const DebitNoteList = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();

  // PDF preview state - status-agnostic preview to check formatting before approval
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [debitNotes, setDebitNotes] = useState([]);
  // Column sorting — comparators live in utils/tableSort, column kinds in
  // ./listColumns, so all six invoicing lists sort by the same rules.
  const { sort, onSort, applySort } = useTableSort(DEBIT_NOTE_COLUMNS);
  const [loading, setLoading] = useState(true);
  // Refusal or failure text for the banner. Without it a 403 renders as
  // an empty table, which reads as "you have none" rather than "we could
  // not ask".
  const [loadError, setLoadError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Search and filter state
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedNoteForAudit, setSelectedNoteForAudit] = useState(null);
  
  // Relationship modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedNoteForRelationship, setSelectedNoteForRelationship] = useState(null);
  
  // Component-level flag to prevent double load (resets on unmount)
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    // Load debit notes only once on initial mount
    if (!isSearchMode && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadDebitNotes();
    }
  }, [isSearchMode]);
  
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadDebitNotes = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await invoicingService.fetchDebitNotes({});
      setDebitNotes(data.results || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading debit notes:', error);
      setLoadError(extractApiError(error, 'The debit notes could not be loaded.'));
      setLoading(false);
    }
  };

  const handleSearchResults = (results) => {
    console.log('handleSearchResults called with', results.length, 'results');
    setSearchResults(results);
    
    if (results.length > 0 || Object.keys(activeFilters).some(key => activeFilters[key])) {
      console.log('ENTERING SEARCH MODE - we have results or filters');
      setIsSearchMode(true);
    } else {
      console.log('NO RESULTS AND NO FILTERS - staying in search mode but showing empty');
      setIsSearchMode(true);
    }
  };

  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    const hasFilters = Object.keys(filters).some(key => filters[key]);
    if (hasFilters) {
      setIsSearchMode(true);
    }
  };

  const handleSearchStateChange = (searching) => {
    setIsSearching(searching);
  };

  const handleClearSearch = () => {
    console.log('handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveFilters({});
    loadDebitNotes();
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount || isNaN(amount)) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const getPaymentStatus = (note) => {
    if (note.reduction_applied_date) {
      return <span className="payment-badge paid">Applied {new Date(note.reduction_applied_date).toLocaleDateString()}</span>;
    }
    return <span className="payment-badge pending">Not Applied</span>;
  };
  
  const toggleDropdown = (noteId) => {
    setActiveDropdown(activeDropdown === noteId ? null : noteId);
  };
  
  const handleEdit = (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/debit-notes/edit/${note.id}`);
  };
  
  const handleDelete = async (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    if (window.confirm(`Are you sure you want to delete debit note ${note.debit_note_number}?`)) {
      try {
        await invoicingService.deleteDebitNote(note.id);
        loadDebitNotes();
      } catch (error) {
        console.error('Error deleting debit note:', error);
        showError('Failed to delete debit note. Please try again.');
      }
    }
  };
  
  const handleDownloadPDF = async (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    if (note.status !== 'APPROVED') {
      showError('Only approved debit notes can be downloaded.');
      return;
    }

    try {
      const blob = await invoicingService.downloadDebitNotePDF(note.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DebitNote_${note.debit_note_number || note.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to download PDF. Please try again.');
    }
  };

  const handlePreviewPDF = async (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    setIsLoadingPreview(true);
    try {
      const blob = await invoicingService.downloadDebitNotePDF(note.id, { preview: true });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setPreviewFileName(`DebitNote_${note.debit_note_number || note.id}.pdf`);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showError('Failed to load PDF preview. Please try again.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setPreviewFileName('');
  };

  const handleMarkApplied = async (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    if (window.confirm('Mark this debit note as applied against the original invoice?')) {
      try {
        await invoicingService.markDebitNoteApplied(note.id, new Date().toISOString().split('T')[0]);
        showSuccess('Debit note marked as applied');
        loadDebitNotes();
      } catch (error) {
        console.error('Error marking debit note as applied:', error);
        showError('Failed to mark debit note as applied. Please try again.');
      }
    }
  };

  const handleViewGlobalAudit = () => {
    setSelectedNoteForAudit(null);
    setShowAuditModal(true);
  };
  
  const handleViewNoteAudit = (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedNoteForAudit(note);
    setShowAuditModal(true);
  };
  
  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setSelectedNoteForAudit(null);
  };
  
  const handleViewRelationships = (note, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedNoteForRelationship(note);
    setShowRelationshipModal(true);
  };
  
  // Sorted wherever the rows came from — the loaded list OR the search
  // results — so the table never behaves differently in the two modes.
  const visibleRows = applySort(isSearchMode ? searchResults : debitNotes);
  // Point the user at the row they just saved. The creators hand the id
  // over as route state; without it the toast names a row this table
  // gives no way to find (ordered by document date, not by recency).
  const { savedId, rowRef } = useSavedRowFlash({ sort, isSearchMode, activeFilters });
  const savedRowClass = (id) => (savedId !== null && String(id) === String(savedId) ? 'row-just-saved' : '');
  // The banner speaks only about a row that is actually on this page. If the
  // saved row was filtered out or fell beyond the fetched page, saying "see
  // below" would point at nothing -- the toast stands alone instead.
  // A DRAFT has no number yet, so the label falls back to generic wording
  // rather than printing 'undefined' at the user.
  const savedRow = savedId === null
    ? null
    : visibleRows.find(r => String(r.id) === String(savedId)) || null;
  const savedRowLabel = savedRow?.debit_note_number || null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container debit-note-screen">
        {/* Header */}
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          backLabel="Back to Invoicing Dashboard"
          title="Debit Notes"
          kicker="Finance"
          subtitle="Supplier reduction claims"
          actions={
            <>
              <button className="btn-secondary" onClick={handleViewGlobalAudit}>
                <FontAwesomeIcon icon={faClipboardList} /> Audit Center
              </button>

              <button className="btn-primary" onClick={() => history.push('/invoicing/debit-notes/create')}>
                <FontAwesomeIcon icon={faPlus} /> Create Debit Note
              </button>
            </>
          }
        />
        
        {/* Search and Filter Controls */}
        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <InvoiceSearchFilter
              onSearchResults={handleSearchResults}
              onFiltersChange={handleFiltersChange}
              isSearching={isSearching}
              onSearchStateChange={handleSearchStateChange}
              onClearSearch={handleClearSearch}
              invoiceType="debit_note"
            />
          </div>
          
          <button 
            className="reload-button"
            onClick={loadDebitNotes}
            title="Reload debit notes"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
        
        {/* Debit Note List */}
        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={loadDebitNotes} />
          {/* Says which entity's book this list is showing. Without it a
              narrowed list reads as "nothing has been raised" — the fault
              reported on 2026-09-03. */}
          <EntityScopeNotice emptyResult={!loading && visibleRows.length === 0} />
          <SavedRowBanner savedId={savedRow ? savedId : null} label={savedRowLabel} />
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading debit notes...</p>
            </div>
          ) : (
            <table className="invoice-list-table">
              <thead>
                <tr>
                  {DEBIT_NOTE_COLUMNS.map((column) => (
                    <SortableTh key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                  {/* Actions holds buttons, not data — nothing to sort by. */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty-state-cell">
                      <div className="empty-state-container">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={faExclamationCircle} />
                        </div>
                        <FinancialsEmptyState
                          label="debit notes"
                          heading="No Debit Notes Yet"
                          blurb="Debit notes will appear here"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(note => (
                    <tr 
                      key={note.id} 
                      ref={rowRef(note.id)}
                      className={savedRowClass(note.id)}
                      onClick={() => history.push(`/invoicing/debit-notes/${note.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="invoice-number-cell">{note.debit_note_number || (note.status === 'DRAFT' ? 'DRAFT' : 'N/A')}</td>
                      <td>
                        <span 
                          className="invoice-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/invoicing/incoming/${note.original_invoice}`);
                          }}
                          title="View original incoming invoice"
                        >
                          {note.original_invoice_number}
                        </span>
                      </td>
                      <td>{note.partner_name || 'N/A'}</td>
                      <td>{note.currency || 'USD'}</td>
                      <td className="amount-cell">{formatCurrency(note.gross_amount, note.currency)}</td>
                      <td>{note.invoice_date ? new Date(note.invoice_date).toLocaleDateString() : 'N/A'}</td>
                      <td className="status-cell">
                        <span className={`status-badge ${note.status?.toLowerCase()}`}>
                          {note.status_display || note.status}
                        </span>
                      </td>
                      <td>
                        {getPaymentStatus(note)}
                      </td>
                      <td className="actions-cell">
                        <div 
                          className={`action-dropdown ${activeDropdown === note.id ? 'dropdown-open' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                          ref={activeDropdown === note.id ? dropdownRef : null}
                        >
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(note.id);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === note.id && (
                            <div className="dropdown-menu">
                              {/* View option always available */}
                              <button onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(null);
                                history.push(`/invoicing/debit-notes/${note.id}`);
                              }}>
                                <FontAwesomeIcon icon={faEye} /> View
                              </button>
                              
                              {/* View Relationships */}
                              <button onClick={(e) => handleViewRelationships(note, e)}>
                                <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                              </button>
                              
                              {/* Preview PDF - any status, no audit-log entry */}
                              <button onClick={(e) => handlePreviewPDF(note, e)} disabled={isLoadingPreview}>
                                <FontAwesomeIcon icon={faFilePdf} /> {isLoadingPreview ? 'Loading...' : 'Preview PDF'}
                              </button>

                              {/* Download PDF - APPROVED only, writes an audit-log entry */}
                              {note.status === 'APPROVED' && (
                                <button onClick={(e) => handleDownloadPDF(note, e)}>
                                  <FontAwesomeIcon icon={faDownload} /> Download PDF
                                </button>
                              )}

                              {/* Mark Applied - only for APPROVED and not yet applied */}
                              {note.status === 'APPROVED' && !note.reduction_applied_date && (
                                <button onClick={(e) => handleMarkApplied(note, e)}>
                                  <FontAwesomeIcon icon={faCheck} /> Mark Applied
                                </button>
                              )}
                              
                              {/* Only show Edit if DRAFT */}
                              {note.status === 'DRAFT' && (
                                <button onClick={(e) => handleEdit(note, e)}>
                                  <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                              )}
                              
                              {/* Audit logs always available */}
                              <button onClick={(e) => handleViewNoteAudit(note, e)}>
                                <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                              </button>
                              
                              {/* Only show Delete if DRAFT */}
                              {note.status === 'DRAFT' && (
                                <>
                                  <hr />
                                  <button className="danger" onClick={(e) => handleDelete(note, e)}>
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Audit Modal */}
      <InvoiceAuditModal
        isOpen={showAuditModal}
        onClose={handleCloseAuditModal}
        invoiceType="debit_note"
        invoiceId={selectedNoteForAudit?.id || null}
        invoiceNumber={selectedNoteForAudit?.debit_note_number || null}
      />
      
      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        invoice={selectedNoteForRelationship}
        invoiceType="debit"
      />

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={showPdfPreview}
        pdfUrl={previewPdfUrl}
        fileName={previewFileName}
        onClose={handleClosePdfPreview}
      />
    </Layout>
  );
};

export default DebitNoteList;
